import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import { plotService } from '../../services/plot.service'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'

interface BurialAsset {
  id: string
  name: string
  type: 'BURIAL_PLOT'
  status: 'ACTIVE' | 'MAINTENANCE' | 'RETIRED' | 'DAMAGED'
  description?: string
  location?: string
  createdAt: string
  updatedAt: string
  images?: string[]

  // Burial plot specific fields
  totalPlots?: number
  availablePlots?: number
  sections?: string[]
  layout?: any
  specifications?: any
  dailyCost?: number
}

interface Plot {
  id: string
  name: string
  plotNumber?: string
  section?: string
  row?: string
  plotSize: 'ADULT' | 'CHILD'
  dimensions?: {
    length?: number
    width?: number
    depth?: number
  }
  capacity?: number
  isAvailable: boolean
  status: string
  assetId: string
  createdAt: string
  updatedAt: string
}

const CemeteryAssetManagementPage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { assetId } = useParams<{ assetId?: string }>()
  const [assets, setAssets] = useState<BurialAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAsset, setEditingAsset] = useState<BurialAsset | null>(null)
  const [formData, setFormData] = useState<Partial<BurialAsset>>({})

  // Helper function to format asset type for display
  const formatAssetType = (type: string) => {
    if (!type) return 'Burial Plot'
    return type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  // Helper function to organize plots by sections and rows
  const organizePlots = (plots: Plot[]) => {
    const organized: Record<string, Record<string, Plot[]>> = {}

    plots.forEach(plot => {
      const section = plot.section || 'Unknown Section'
      const row = plot.row || 'Unknown Row'

      if (!organized[section]) {
        organized[section] = {}
      }

      if (!organized[section][row]) {
        organized[section][row] = []
      }

      organized[section][row].push(plot)
    })

    // Sort plots within each row by plot number (numerical)
    Object.keys(organized).forEach(section => {
      Object.keys(organized[section]).forEach(row => {
        organized[section][row].sort((a, b) => {
          const plotNumberA = a.plotNumber || ''
          const plotNumberB = b.plotNumber || ''

          // Try to extract numeric part for proper numerical sorting
          const numA = parseInt(plotNumberA.replace(/\D/g, ''), 10) || 0
          const numB = parseInt(plotNumberB.replace(/\D/g, ''), 10) || 0

          if (numA !== numB) {
            return numA - numB
          }

          // If numeric parts are equal, sort alphabetically
          return plotNumberA.localeCompare(plotNumberB)
        })
      })

      // Sort rows within each section (alphabetically or numerically)
      const sortedRows = Object.entries(organized[section])
        .sort(([rowA], [rowB]) => {
          // Try numeric sort first
          const numA = parseInt(rowA.replace(/\D/g, ''), 10)
          const numB = parseInt(rowB.replace(/\D/g, ''), 10)

          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB
          }

          // Fall back to alphabetical sort
          return rowA.localeCompare(rowB)
        })

      const reorderedSection: Record<string, Plot[]> = {}
      sortedRows.forEach(([row, plots]) => {
        reorderedSection[row] = plots
      })

      organized[section] = reorderedSection
    })

    // Sort sections alphabetically or numerically
    const sortedSections = Object.entries(organized)
      .sort(([sectionA], [sectionB]) => {
        // Try numeric sort first
        const numA = parseInt(sectionA.replace(/\D/g, ''), 10)
        const numB = parseInt(sectionB.replace(/\D/g, ''), 10)

        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB
        }

        // Fall back to alphabetical sort
        return sectionA.localeCompare(sectionB)
      })

    const reordered: Record<string, Record<string, Plot[]>> = {}
    sortedSections.forEach(([section, rows]) => {
      reordered[section] = rows
    })

    return reordered
  }

  const [saving, setSaving] = useState(false)

  // Plot management state
  const [plots, setPlots] = useState<Plot[]>([])
  const [activeView, setActiveView] = useState<'overview' | 'plots'>('overview')
  const [showAddPlotForm, setShowAddPlotForm] = useState(false)
  const [plotFormData, setPlotFormData] = useState({
    plotNumber: '',
    section: '',
    row: '',
    plotSize: 'ADULT' as 'ADULT' | 'CHILD',
    isAvailable: true,
    dimensions: {
      length: 2.5,
      width: 1.2,
      depth: 1.8
    }
  })

  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    MAINTENANCE: 'bg-yellow-100 text-yellow-800',
    RETIRED: 'bg-gray-100 text-gray-800',
    DAMAGED: 'bg-red-100 text-red-800'
  }

  const statusIcons = {
    ACTIVE: CheckCircleIcon,
    MAINTENANCE: XCircleIcon,
    RETIRED: XCircleIcon,
    DAMAGED: XCircleIcon
  }

  useEffect(() => {
    if (assetId && assetId !== 'new') {
      loadAsset(assetId)
    } else {
      setLoading(false)
    }
  }, [assetId])

  // Load plots only for burial plot assets
  useEffect(() => {
    if (assetId && assetId !== 'new' && editingAsset?.type === 'BURIAL_PLOT') {
      loadPlots(assetId)
    }
  }, [assetId, editingAsset])

  const loadAsset = async (id: string) => {
    try {
      setLoading(true)
      const response = await api.get(`/provider-dashboard/assets/${id}`)
      setEditingAsset(response.data)
      setFormData(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load burial plot')
      navigate('/provider-dashboard/assets')
    } finally {
      setLoading(false)
    }
  }

  const loadPlots = async (assetId: string) => {
    try {
      const response = await plotService.getAssetPlots(assetId)
      if (response.data) {
        setPlots(response.data)
      }
    } catch (error: any) {
      console.error('Error loading plots:', error)
      // Don't show error toast for plots loading as it's optional
    }
  }

  const fetchAssets = async () => {
    try {
      const response = await api.get('/provider-dashboard/burial-plots')
      setAssets(response.data)
    } catch (err: any) {
      console.log('Error fetching burial assets:', err.message || err)
      setAssets([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!assetId) {
      fetchAssets()
    }
  }, [assetId])

  const handleAddAsset = () => {
    setEditingAsset(null)
    setFormData({
      name: '',
      type: 'BURIAL_PLOT',
      status: 'ACTIVE',
      description: '',
      location: '',
      dailyCost: 0,
      totalPlots: 0,
      availablePlots: 0,
      sections: [],
    })
    navigate('/provider-dashboard/assets/new')
  }

  const handleEditAsset = (asset: BurialAsset) => {
    navigate(`/provider-dashboard/assets/${asset.id}`)
  }

  const handleBackToList = () => {
    navigate('/provider-dashboard/assets')
    setEditingAsset(null)
    setFormData({})
    setPlots([])
    setActiveView('overview')
  }

  const handleSaveAsset = async () => {
    setSaving(true)
    try {
      let response

      if (editingAsset) {
        response = await api.put(`/provider-dashboard/assets/${editingAsset.id}`, formData)
        toast.success('Burial plot updated successfully')
      } else {
        response = await api.post('/provider-dashboard/assets', formData)
        toast.success('Burial plot added successfully')
      }

      if (!assetId || assetId === 'new') {
        fetchAssets()
      }

      handleBackToList()
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save burial plot')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAsset = async (assetId: string) => {
    if (!confirm('Are you sure you want to delete this burial plot?')) return

    try {
      await api.delete(`/provider-dashboard/assets/${assetId}`)
      setAssets(assets.filter(a => a.id !== assetId))
      toast.success('Burial plot deleted successfully')
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to delete burial plot')
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePlotCreate = async () => {
    try {
      const response = await plotService.createPlot(assetId!, plotFormData)
      if (response.data) {
        await loadPlots(assetId!)
        setShowAddPlotForm(false)
        setPlotFormData({
          plotNumber: '',
          section: '',
          row: '',
          plotSize: 'ADULT',
          isAvailable: true,
          dimensions: {
            length: 2.5,
            width: 1.2,
            depth: 1.8
          }
        })
        toast.success('Plot added successfully')
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add plot')
    }
  }

  const handlePlotSizeChange = (plotSize: 'ADULT' | 'CHILD') => {
    const defaultDimensions = plotSize === 'ADULT'
      ? { length: 2.5, width: 1.2, depth: 1.8 }
      : { length: 1.8, width: 0.9, depth: 1.2 }

    setPlotFormData(prev => ({
      ...prev,
      plotSize,
      dimensions: defaultDimensions
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  // Show edit/create form
  if (assetId) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBackToList}
                className="text-gray-600 hover:text-gray-900 flex items-center"
              >
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                Back to Burial Plots
              </button>
              <div className="bg-green-100 p-3 rounded-full">
                <BuildingOfficeIcon className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {editingAsset ? 'Edit Burial Plot' : 'Add New Burial Plot'}
                </h1>
                <p className="text-gray-600">Manage your cemetery burial plots</p>
              </div>
            </div>
            {editingAsset && (
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveView('overview')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeView === 'overview'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveView('plots')}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    activeView === 'plots'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Plots ({plots.length})
                </button>
              </div>
            )}
          </div>
        </div>

        {activeView === 'overview' ? (
          /* Burial Plot Form */
          <div className="bg-white rounded-lg shadow p-6">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plot Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    value={formData.status || ''}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    required
                  >
                    {Object.keys(statusColors).map(status => (
                      <option key={status} value={status}>
                        {status.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Plots
                  </label>
                  <input
                    type="number"
                    value={formData.totalPlots || ''}
                    onChange={(e) => handleInputChange('totalPlots', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="Total number of plots"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Plots
                  </label>
                  <input
                    type="number"
                    value={formData.availablePlots || ''}
                    onChange={(e) => handleInputChange('availablePlots', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="Number of available plots"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Daily Cost (RM)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.dailyCost || ''}
                    onChange={(e) => handleInputChange('dailyCost', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    placeholder="e.g., Kuala Lumpur, Malaysia"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={handleBackToList}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAsset}
                disabled={saving}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  editingAsset ? 'Update Burial Plot' : 'Add Burial Plot'
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Plots Management */
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">Individual Plots</h2>
              <button
                onClick={() => setShowAddPlotForm(true)}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Add Plot</span>
              </button>
            </div>

            {showAddPlotForm && (
              <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h3 className="text-md font-medium text-gray-900 mb-4">Add New Plot</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plot Number
                    </label>
                    <input
                      type="text"
                      value={plotFormData.plotNumber}
                      onChange={(e) => setPlotFormData(prev => ({ ...prev, plotNumber: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="e.g., A-123"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section
                    </label>
                    <input
                      type="text"
                      value={plotFormData.section}
                      onChange={(e) => setPlotFormData(prev => ({ ...prev, section: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="e.g., Section A"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Row
                    </label>
                    <input
                      type="text"
                      value={plotFormData.row}
                      onChange={(e) => setPlotFormData(prev => ({ ...prev, row: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                      placeholder="e.g., Row 5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plot Size
                    </label>
                    <select
                      value={plotFormData.plotSize}
                      onChange={(e) => handlePlotSizeChange(e.target.value as 'ADULT' | 'CHILD')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                    >
                      <option value="ADULT">Adult</option>
                      <option value="CHILD">Child</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowAddPlotForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePlotCreate}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                  >
                    Add Plot
                  </button>
                </div>
              </div>
            )}

            {plots.length === 0 ? (
              <div className="text-center py-8">
                <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No plots yet</h3>
                <p className="mt-1 text-sm text-gray-500">Add your first plot to get started.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(organizePlots(plots)).map(([section, rows]) => (
                  <div key={section} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Section Header */}
                    <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        <BuildingOfficeIcon className="h-5 w-5 mr-2 text-gray-600" />
                        Section {section}
                      </h3>
                    </div>

                    {/* Rows within Section */}
                    <div className="p-6 space-y-6">
                      {Object.entries(rows).map(([row, rowPlots]) => (
                        <div key={`${section}-${row}`} className="border-l-4 border-blue-200 pl-4">
                          {/* Row Header */}
                          <div className="mb-3">
                            <h4 className="text-md font-medium text-gray-800 flex items-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold mr-2">
                                {row}
                              </span>
                              Row {row}
                              <span className="ml-2 text-sm text-gray-500">
                                ({rowPlots.length} plots)
                              </span>
                            </h4>
                          </div>

                          {/* Plots within Row */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {rowPlots.map((plot) => (
                              <div key={plot.id} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex-1">
                                    <h5 className="font-medium text-gray-900 text-sm">
                                      {plot.plotNumber || `Plot ${plot.id.slice(-6)}`}
                                    </h5>
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    plot.isAvailable
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {plot.isAvailable ? 'Available' : 'Occupied'}
                                  </span>
                                </div>
                                <div className="text-xs text-gray-600">
                                  <p>Size: {plot.plotSize}</p>
                                  {plot.dimensions && plot.dimensions.length && plot.dimensions.width && (
                                    <p>
                                      {plot.dimensions.length}m × {plot.dimensions.width}m
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Show list view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-green-100 p-3 rounded-full">
              <BuildingOfficeIcon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Burial Plot Management</h1>
              <p className="text-gray-600">Manage your cemetery burial plots and grave sites</p>
            </div>
          </div>
          <button
            onClick={handleAddAsset}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Burial Plot</span>
          </button>
        </div>
      </div>

      {/* Burial Plots Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assets.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No burial plots</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first burial plot.</p>
            <div className="mt-6">
              <button
                onClick={handleAddAsset}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Burial Plot
              </button>
            </div>
          </div>
        ) : (
          assets.map((plot) => {
            const StatusIcon = statusIcons[plot.status as keyof typeof statusIcons] || statusIcons.ACTIVE

            return (
              <div key={plot.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <BuildingOfficeIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-medium text-gray-900">{plot.name}</h3>
                      <p className="text-sm text-gray-500">{formatAssetType(plot.type || 'BURIAL_PLOT')}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[plot.status as keyof typeof statusColors] || statusColors.ACTIVE}`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {plot.status || 'ACTIVE'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {plot.totalPlots && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Total Plots:</span>
                      <span className="ml-2">{plot.totalPlots}</span>
                    </div>
                  )}
                  {plot.availablePlots && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Available:</span>
                      <span className="ml-2 text-green-600">{plot.availablePlots}</span>
                    </div>
                  )}
                  {plot.dailyCost && (
                    <div className="flex items-center text-sm text-gray-600">
                      <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                      <span className="font-medium">Daily Cost:</span>
                      <span className="ml-2">RM{plot.dailyCost}</span>
                    </div>
                  )}
                  {plot.location && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium">Location:</span>
                      <span className="ml-2">{plot.location}</span>
                    </div>
                  )}
                </div>

                {plot.description && (
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">{plot.description}</p>
                )}

                <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditAsset(plot)}
                      className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center"
                    >
                      <PencilIcon className="h-4 w-4 mr-1" />
                      Manage
                    </button>
                    <button
                      onClick={() => handleDeleteAsset(plot.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default CemeteryAssetManagementPage